/**
 * Buy Score composition rule card per scope §6.6.5. Atlas Day 3a P1.
 *
 * Surface per D1 (α) catalog-only lock (Atlas Day 2 + Day 2.5): "Buy Score
 * — weighted blend of N active metrics. [Active metric labels]." No weight
 * values surfaced; weights live per-session in buying_sessions.ui_state.weights
 * per scope §2 engine-only principle.
 *
 * Substrate-driven from bswc active rows (Atlas Day 2 Phase 0 #11 verified
 * 7 active labels at session-open substrate state).
 *
 * Click handler navigates to inspect view per Atlas Day 2.5 P1 URL routing.
 */

import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale } from 'lucide-react';
import type { BuyScoreCompositionSubstrate } from '../../../lib/atlas/transformationRowSubstrate';

export interface BuyScoreCompositionCardProps {
  substrate: BuyScoreCompositionSubstrate;
}

// Natural-language Oxford-comma joiner (mirrors titleBlockSubstrate.joinList)
function joinList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

export default function BuyScoreCompositionCard({
  substrate,
}: BuyScoreCompositionCardProps): JSX.Element {
  const navigate = useNavigate();
  const { activeBswcLabels } = substrate;
  return (
    <button
      type="button"
      data-sub-region-id="buy-score-composition"
      onClick={() => navigate('/admin/engine-atlas/inspect/buy-score-composition')}
      className="w-full text-left border border-border-subtle bg-surface-0 rounded-md p-3 hover:border-accent transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-2">
        <Scale size={16} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-ink-bright">Buy Score composition</h3>
          <p className="text-xs text-ink-secondary mt-1">
            Buy Score — weighted blend of {activeBswcLabels.length} active{' '}
            {activeBswcLabels.length === 1 ? 'metric' : 'metrics'}:{' '}
            {joinList(activeBswcLabels)}.
          </p>
        </div>
      </div>
    </button>
  );
}
